import { useCallback, useEffect, useState } from 'react';
import './FamilyDetails.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://my-chsapi.onrender.com';

function FamilyDetails({
  onBackToDashboard,
  onRequireLogin,
}) {
  const cnicPattern = /^(\d{13}|\d{5}-\d{7}-\d)$/;
  const namePattern = /^[A-Za-z][A-Za-z\s.'-]{1,59}$/;
  const phonePattern = /^\+?\d{10,15}$/;

  const [records, setRecords] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editingRecord, setEditingRecord] = useState(null);
  const [editForm, setEditForm] = useState({ residentName: '', flatNumber: '', familyMembers: [] });
  const [savingEdit, setSavingEdit] = useState(false);
  useEffect(() => {
    const user = localStorage.getItem('user');
    if (!user && onRequireLogin) {
      onRequireLogin();
    }
  }, [onRequireLogin]);

  const fetchFamilyRecords = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (searchQuery.trim()) {
        params.append('search', searchQuery.trim());
      }
      if (statusFilter === 'all') {
        params.append('includeInactive', 'true');
      }
      const res = await fetch(`${API_BASE_URL}/api/family?${params.toString()}`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) {
        setRecords(Array.isArray(data.records) ? data.records : []);
      } else {
        setError(data.message || 'Failed to fetch family details.');
      }
    } catch (_err) {
      setError('Failed to fetch family details.');
    }
    setLoading(false);
  }, [searchQuery, statusFilter]);

  useEffect(() => {
    fetchFamilyRecords();

    const intervalId = setInterval(() => {
      fetchFamilyRecords();
    }, 30000);

    return () => {
      clearInterval(intervalId);
    };
  }, [fetchFamilyRecords]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchFamilyRecords();
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
  };

  const openEditModal = (record) => {
    const members = Array.isArray(record.familyMembers) ? record.familyMembers : [];
    setEditingRecord(record);
    setEditForm({
      residentName: record.residentName || '',
      flatNumber: record.flatNumber || '',
      familyMembers: members.map((m) => ({
        memberName: m.memberName || m.name || '',
        relation: m.relation || '',
        cnic: m.cnic || '',
        phone: m.phone || '',
      })),
    });
  };

  const closeEditModal = () => {
    setEditingRecord(null);
    setEditForm({ residentName: '', flatNumber: '', familyMembers: [] });
    setSavingEdit(false);
  };

  const updateEditMember = (index, key, value) => {
    setEditForm((prev) => ({
      ...prev,
      familyMembers: prev.familyMembers.map((m, i) => (i === index ? { ...m, [key]: value } : m)),
    }));
  };

  const addEditMember = () => {
    setEditForm((prev) => ({
      ...prev,
      familyMembers: [...prev.familyMembers, { memberName: '', relation: '', cnic: '', phone: '' }],
    }));
  };

  const removeEditMember = (index) => {
    setEditForm((prev) => ({
      ...prev,
      familyMembers: prev.familyMembers.filter((_, i) => i !== index),
    }));
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editingRecord?._id) return;

    const residentName = editForm.residentName.trim();
    const flatNumber = editForm.flatNumber.trim();
    if (!residentName || !flatNumber) {
      setError('Resident name and flat number are required for update.');
      return;
    }

    if (!namePattern.test(residentName)) {
      setError('Resident name must be 2-60 characters and contain letters only.');
      return;
    }

    if (!/\d/.test(flatNumber) || flatNumber.length > 20) {
      setError('Flat number must include digits and be at most 20 characters.');
      return;
    }

    const cleanedMembers = (Array.isArray(editForm.familyMembers) ? editForm.familyMembers : [])
      .map((member) => ({
        memberName: String(member.memberName || member.name || '').trim(),
        relation: String(member.relation || '').trim(),
        cnic: String(member.cnic || '').trim(),
        phone: String(member.phone || '').trim(),
      }))
      .filter((member) => member.memberName || member.relation || member.cnic || member.phone);

    const invalidMember = cleanedMembers.find((member) => {
      if (!member.memberName || !namePattern.test(member.memberName)) return true;
      if (!member.relation || member.relation.length < 2 || member.relation.length > 30) return true;
      if (!cnicPattern.test(member.cnic)) return true;
      if (member.phone && !phonePattern.test(member.phone)) return true;
      return false;
    });

    if (invalidMember) {
      setError('Each member needs valid name, relation, CNIC format, and optional phone format.');
      return;
    }

    const seen = new Set();
    const hasDuplicateCnic = cleanedMembers.some((member) => {
      const key = member.cnic.replace(/\D/g, '');
      if (!key) return false;
      if (seen.has(key)) return true;
      seen.add(key);
      return false;
    });

    if (hasDuplicateCnic) {
      setError('Duplicate CNIC is not allowed in family members.');
      return;
    }

    setSavingEdit(true);
    setError('');
    try {
      const payload = {
        residentName,
        flatNumber,
        familyMembers: cleanedMembers.map((member) => ({
          memberName: member.memberName || member.name || '',
          name: member.memberName || member.name || '',
          relation: member.relation || '',
          cnic: member.cnic || '',
          phone: member.phone || '',
        })),
      };
      const res = await fetch(`${API_BASE_URL}/api/family/${editingRecord._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to update family details.');
      }
      closeEditModal();
      fetchFamilyRecords();
    } catch (err) {
      setError(err.message || 'Failed to update family details.');
    }
    setSavingEdit(false);
  };

  const handleDeleteRecord = async (record) => {
    if (!record?._id) return;

    const confirmed = window.confirm(`Delete family record for ${record.residentName || 'this resident'}?`);
    if (!confirmed) return;

    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/family/${record._id}/status`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: !(record.isActive !== false) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to update family status.');
      }
      if (editingRecord?._id === record._id) {
        closeEditModal();
      }
      fetchFamilyRecords();
    } catch (err) {
      setError(err.message || 'Failed to update family status.');
    }
    setLoading(false);
  };

  const handlePrintReport = () => {
    const printWindow = window.open('', '_blank', 'width=1200,height=800');
    if (!printWindow) return;

    const rowsHtml = records.length
      ? records
          .map((record) => {
            const members = Array.isArray(record.familyMembers) ? record.familyMembers : [];
            return `
              <tr>
                <td>${record.residentName || '-'}</td>
                <td>${record.flatNumber || '-'}</td>
                <td>${members.length}</td>
                <td>${record.uploadedAt ? new Date(record.uploadedAt).toLocaleString() : '-'}</td>
                <td>${record.isActive !== false ? 'Active' : 'Inactive'}</td>
              </tr>
            `;
          })
          .join('')
      : '<tr><td colspan="5" style="text-align:center; padding:16px;">No records found</td></tr>';

    const printedAt = new Date().toLocaleString();

    printWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <title>Family Details Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 24px; color: #111; }
            h1 { margin: 0 0 8px 0; font-size: 22px; }
            .meta { margin-bottom: 16px; font-size: 13px; color: #444; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background: #f2f7fb; }
          </style>
        </head>
        <body>
          <h1>Family Details Report</h1>
          <div class="meta">Printed: ${printedAt}</div>
          <div class="meta">Records: ${records.length}</div>
          <table>
            <thead>
              <tr>
                <th>Resident Name</th>
                <th>Flat Number</th>
                <th>Members</th>
                <th>Uploaded At</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>${rowsHtml}</tbody>
          </table>
          <script>
            window.onload = function () {
              window.print();
              window.onafterprint = function () { window.close(); };
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <div className="family-report-container">
      <div className="bg-shapes">
        <div className="shape shape-1" />
        <div className="shape shape-2" />
        <div className="shape shape-3" />
      </div>

      <div className="family-report-header">
        <div className="family-report-title">
          <button className="back-btn" onClick={onBackToDashboard}>
            ←
          </button>
          <div>
            <h1>Family Details</h1>
            <p className="subtitle">View uploaded family CNIC records</p>
          </div>
        </div>
      </div>

      <div className="family-report-content">
        <div className="family-filters-section">
          <form className="family-filter-row" onSubmit={handleSearch}>
            <div className="family-search-group">
              <label>Search</label>
              <input
                type="text"
                placeholder="Search by resident name, flat number, file name"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="family-search-input"
              />
            </div>
            <button className="family-search-btn" type="submit">Search</button>
            <button className="family-refresh-btn" type="button" onClick={fetchFamilyRecords}>Refresh</button>
            <div className="family-status-filter">
              <label>Status</label>
              <select value={statusFilter} onChange={(e) => handleStatusFilterChange(e.target.value)} className="family-status-select">
                <option value="active">Active Only</option>
                <option value="inactive">Inactive Only</option>
                <option value="all">All</option>
              </select>
            </div>
          </form>
          <div className="family-results-count">
            {loading ? 'Loading...' : `Showing ${records.length} records`}
            {error && <span className="family-error">{error}</span>}
          </div>
        </div>

        <div className="family-table-wrapper">
          <table className="family-report-table">
            <thead>
              <tr>
                <th>Resident Name</th>
                <th>Flat Number</th>
                <th>Members</th>
                <th>Uploaded At</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {records.length > 0 ? (
                records.map((record) => {
                  const members = Array.isArray(record.familyMembers) ? record.familyMembers : [];
                  const uploadedAt = record.uploadedAt
                    ? new Date(record.uploadedAt).toLocaleString()
                    : '-';
                  const isActive = record.isActive !== false;

                  return (
                    <tr key={String(record._id || `${record.flatNumber}-${record.uploadedAt}`)}>
                      <td>{record.residentName || '-'}</td>
                      <td>{record.flatNumber || '-'}</td>
                      <td>{members.length}</td>
                      <td>{uploadedAt}</td>
                      <td>
                        <span className={`family-status-badge ${isActive ? 'active' : 'inactive'}`}>
                          {isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <button className="family-edit-btn" onClick={() => openEditModal(record)}>
                          Edit
                        </button>
                        <button className="family-delete-btn" onClick={() => handleDeleteRecord(record)}>
                          {record.isActive !== false ? 'Deactivate' : 'Activate'}
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="6" className="family-no-results">No family records found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="family-report-actions">
        <button className="family-print-btn" onClick={handlePrintReport}>💾 Save PDF</button>
        <button className="family-print-btn" onClick={handlePrintReport}>🖨️ Print Report</button>
        <button className="family-back-btn" onClick={onBackToDashboard}>Back to Dashboard</button>
      </div>

      {editingRecord && (
        <div className="family-edit-overlay" onClick={closeEditModal}>
          <div className="family-edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="family-edit-header">
              <h3>Edit Family Details</h3>
              <button className="family-close-btn" onClick={closeEditModal}>×</button>
            </div>

            <form onSubmit={handleSaveEdit} className="family-edit-form">
              <div className="family-edit-grid">
                <label>
                  Resident Name
                  <input
                    type="text"
                    value={editForm.residentName}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, residentName: e.target.value }))}
                    required
                  />
                </label>
                <label>
                  Flat Number
                  <input
                    type="text"
                    value={editForm.flatNumber}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, flatNumber: e.target.value }))}
                    required
                  />
                </label>
              </div>

              <div className="family-members-edit-wrap">
                <div className="family-members-edit-head">
                  <strong>Family Members</strong>
                  <button type="button" className="family-add-member-btn" onClick={addEditMember}>+ Add Member</button>
                </div>

                {editForm.familyMembers.map((member, index) => (
                  <div className="family-member-edit-card" key={`edit-member-${index}`}>
                    <input
                      type="text"
                      placeholder="Member Name"
                      value={member.memberName}
                      onChange={(e) => updateEditMember(index, 'memberName', e.target.value)}
                    />
                    <input
                      type="text"
                      placeholder="Relation"
                      value={member.relation}
                      onChange={(e) => updateEditMember(index, 'relation', e.target.value)}
                    />
                    <input
                      type="text"
                      placeholder="CNIC"
                      value={member.cnic}
                      onChange={(e) => updateEditMember(index, 'cnic', e.target.value)}
                    />
                    <input
                      type="text"
                      placeholder="Phone"
                      value={member.phone}
                      onChange={(e) => updateEditMember(index, 'phone', e.target.value)}
                    />
                    <button type="button" className="family-remove-member-btn" onClick={() => removeEditMember(index)}>
                      Remove
                    </button>
                  </div>
                ))}
              </div>

              <div className="family-edit-actions">
                <button type="button" className="family-cancel-btn" onClick={closeEditModal}>Cancel</button>
                <button type="submit" className="family-save-btn" disabled={savingEdit}>
                  {savingEdit ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default FamilyDetails;
